import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Panel, Label, Glyphicon } from 'react-bootstrap';
import request from 'axios';
import Moment from 'moment';
import Button from './common/Button';
import CheckBox from './common/CheckBox';
import TextInput from './common/TextInput';
import ConfirmDialog from './common/ConfirmDialog';
import LoadingProgressBar from './common/LoadingProgressBar';
import { caseSetServerError } from '../actions/caseActions';
import { isAdmin, isDemoAccount, getDateFromUTC } from '../helpers/utils';
import { getClientAuthHeader, beginImpersonation } from '../../client/token';


export const formatBytes = (bytes) => {
    if (!bytes) {
        return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, exp);
    return `${value >= 100 ? Math.round(value) : value.toFixed(1)} ${units[exp]}`;
};


const userMatchesFilter = (user, filter) => {
    if (!filter) {
        return true;
    }

    const haystack = `${user.id} ${user['FirstName'] || ''} ${user['LastName'] || ''} ${user['Email'] || ''}`.toLowerCase();
    return haystack.indexOf(filter) !== -1;
};


class AdminDashboard extends PureComponent {
    constructor(props) {
        super(props);
        this.impersonate = this.impersonate.bind(this);
        this.deleteUserConfirm = this.deleteUserConfirm.bind(this);
        this.deleteUser = this.deleteUser.bind(this);
        this.clearDemoAccounts = this.clearDemoAccounts.bind(this);
        this.state = {
            users: undefined,
            stats: undefined,
            statsError: undefined,
            loading: false,
            deleteCandidate: undefined,
            showDemo: false,
            filter: '',
            clearDemoDialog: false,
        };
    }

    componentDidMount() {
        this.loadUsers();
        this.loadStats();
    }

    async loadUsers() {
        try {
            const result = await request.get('/api/v1/admin/users', getClientAuthHeader());
            this.setState({ users: result.data.users });
        } catch (error) {
            console.error(new Error().stack, error);
            this.props.dispatch(caseSetServerError('Loading users failed. Are you logged in as admin?'));
        }
    }

    async loadStats() {
        try {
            const result = await request.get('/api/v1/admin/stats', getClientAuthHeader());
            this.setState({ stats: result.data });
        } catch (error) {
            console.error(new Error().stack, error);
            this.setState({ statsError: 'Stats unavailable' });
        }
    }

    async impersonate(user) {
        this.setState({ loading: true });
        try {
            const result = await request.post('/api/v1/user/employee/impersonate', { id: user.id }, getClientAuthHeader());
            beginImpersonation(result.data.token);
            // Full page load so the whole Redux state is rebuilt for the
            // impersonated user (cases, contacts, config, ...).
            window.location.assign('/case/all');
        } catch (error) {
            console.error(new Error().stack, error);
            this.props.dispatch(caseSetServerError('Impersonation failed.'));
            this.setState({ loading: false });
        }
    }

    deleteUserConfirm(user) {
        this.setState({ deleteCandidate: user });
    }

    async deleteUser() {
        const user = this.state.deleteCandidate;
        this.setState({ loading: true, deleteCandidate: undefined });
        try {
            await request.post('/api/v1/user/employee/delete', { id: user.id }, getClientAuthHeader());
            await this.loadUsers();
        } catch (error) {
            console.error(new Error().stack, error);
            this.props.dispatch(caseSetServerError('Deleting user failed.'));
        }

        this.setState({ loading: false });
    }

    async clearDemoAccounts() {
        this.setState({ loading: true, clearDemoDialog: false });
        try {
            const result = await request.post('/api/v1/admin/demo/clear', {}, getClientAuthHeader());
            await Promise.all([this.loadUsers(), this.loadStats()]);
            console.log(`Purged ${result.data.purged} demo account(s).`);
        } catch (error) {
            console.error(new Error().stack, error);
            this.props.dispatch(caseSetServerError('Clearing demo accounts failed.'));
        }

        this.setState({ loading: false });
    }

    renderStatCard(label, value, icon) {
        return (
            <Col xs={6} md={3} key={label}>
                <Panel data-hj-whitelist>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 28, fontWeight: 'bold' }}>
                            {value === undefined ? (this.state.statsError ? '—' : '…') : value}
                        </div>
                        <div style={{ color: '#888' }}><Glyphicon glyph={icon} /> {label}</div>
                    </div>
                </Panel>
            </Col>
        );
    }

    renderUserRow(user, isEmployee, ownerStats) {
        const deleted = Boolean(user.deleted_at);
        const demo = isDemoAccount(user);
        const name = `${user['FirstName'] || ''} ${user['LastName'] || ''}`.trim() || '(no name)';
        const lastActivity = user['$LastActivity$'];
        return (
            <tr key={user.id} style={deleted ? { color: '#aaa' } : undefined}>
                <td style={{ whiteSpace: 'nowrap' }}>{user.id}</td>
                <td style={{ paddingLeft: isEmployee ? 30 : undefined }}>
                    {isEmployee && <Glyphicon glyph="arrow-right" style={{ marginRight: 6, color: '#bbb' }} />}
                    <strong style={{ fontWeight: isEmployee ? 'normal' : 'bold' }}>{name}</strong>
                    {' '}<span style={{ color: '#888' }}>{user['Email']}</span>
                    {demo && <span>{' '}<Label bsStyle="warning">demo</Label></span>}
                    {deleted && <span>{' '}<Label bsStyle="danger">deleted</Label></span>}
                </td>
                <td>{!isEmployee && user['CompanyName']}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{getDateFromUTC(user['created_at'])}</td>
                <td style={{ whiteSpace: 'nowrap' }} title={lastActivity ? Moment.utc(lastActivity).format('M/D/YYYY HH:mm UTC') : undefined}>
                    {lastActivity ? Moment.utc(lastActivity).fromNow() : ''}
                </td>
                <td style={{ textAlign: 'right' }}>{user['$LoginCount$'] || 0}</td>
                <td style={{ textAlign: 'right' }}>{ownerStats ? ownerStats.cases : ''}</td>
                <td style={{ textAlign: 'right' }}>{ownerStats ? ownerStats.files : ''}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{ownerStats ? formatBytes(ownerStats.size) : ''}</td>
                <td style={{ textAlign: 'right' }}>{ownerStats ? ownerStats.sms : ''}</td>
                <td style={{ textAlign: 'right' }}>{ownerStats ? ownerStats.emails : ''}</td>
                <td style={{ textAlign: 'right' }}>{ownerStats ? ownerStats.faxes : ''}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                    <Button bsStyle="primary"
                            bsSize="xsmall"
                            label="Impersonate"
                            disabled={this.state.loading || deleted}
                            onClick={() => this.impersonate(user)} />
                    {' '}
                    <Button bsStyle="danger"
                            bsSize="xsmall"
                            label="Delete"
                            disabled={this.state.loading || deleted}
                            onClick={() => this.deleteUserConfirm(user)} />
                </td>
            </tr>
        );
    }

    render() {
        const { loggedIn } = this.props;
        if (loggedIn && !isAdmin(loggedIn)) {
            return (<h5 style={{ margin: 20 }}>Access denied.</h5>);
        }

        const users = this.state.users;
        const stats = this.state.stats;
        const casesPerOwner = (stats && stats.casesPerOwner) || {};
        const filesPerOwner = (stats && stats.filesPerOwner) || {};
        const smsPerOwner = (stats && stats.smsPerOwner) || {};
        const emailPerOwner = (stats && stats.emailPerOwner) || {};
        const faxPerOwner = (stats && stats.faxPerOwner) || {};
        const filter = this.state.filter.trim().toLowerCase();

        const demoCount = users ? users.filter(u => isDemoAccount(u) && !u.deleted_at).length : 0;
        // Only temporary "_demo_" visitor accounts get purged by the clear
        // button; coupon-based demo signups are real accounts.
        const tempDemoCount = users
            ? users.filter(u => isDemoAccount(u) && u['$Subscription$'] && u['$Subscription$'].demoTemp && !u.deleted_at).length
            : 0;

        let rows = [];
        if (users) {
            const visible = this.state.showDemo ? users : users.filter(u => !isDemoAccount(u));
            const owners = visible.filter(u => u.owner_id === 0).sort((a, b) => a.id - b.id);
            const ownerIds = {};
            owners.forEach(o => { ownerIds[o.id] = true; });
            const employeesOf = (ownerId) => visible
                .filter(u => u.owner_id === ownerId)
                .sort((a, b) => a.id - b.id);

            owners.forEach(owner => {
                const employees = employeesOf(owner.id);
                const ownerMatches = userMatchesFilter(owner, filter);
                // A matching owner shows its whole team; otherwise show the
                // owner as context above its matching employees only.
                const matchingEmployees = ownerMatches ? employees : employees.filter(e => userMatchesFilter(e, filter));
                if (!ownerMatches && matchingEmployees.length === 0) {
                    return;
                }

                const files = filesPerOwner[owner.id] || { files: 0, size: 0 };
                rows.push(this.renderUserRow(owner, false, {
                    cases: casesPerOwner[owner.id] || 0,
                    files: files.files,
                    size: files.size,
                    sms: smsPerOwner[owner.id] || 0,
                    emails: emailPerOwner[owner.id] || 0,
                    faxes: faxPerOwner[owner.id] || 0,
                }));

                matchingEmployees.forEach(employee => {
                    rows.push(this.renderUserRow(employee, true));
                });
            });

            // Employees whose owner account no longer exists.
            visible.filter(u => u.owner_id !== 0 && !ownerIds[u.owner_id] && userMatchesFilter(u, filter))
                   .forEach(orphan => {
                       rows.push(this.renderUserRow(orphan, true));
                   });
        }

        // Demo visitors are not real users — the headline count excludes them.
        const activeUsersCount = users && users.filter(u => !u.deleted_at && !isDemoAccount(u)).length;
        const deleteCandidate = this.state.deleteCandidate;
        const deleteIsOwner = deleteCandidate && deleteCandidate.owner_id === 0;

        return (
            <div style={{ margin: '0 20px' }}>
                <Row>
                    {this.renderStatCard('Users', activeUsersCount, 'user')}
                    {this.renderStatCard('Cases', stats && stats.totalCases, 'briefcase')}
                    {this.renderStatCard('Files Stored', stats && stats.totalFiles, 'file')}
                    {this.renderStatCard('Size on Disk', stats && formatBytes(stats.totalSize), 'hdd')}
                </Row>
                <Row>
                    {this.renderStatCard('SMS Sent', stats && stats.totalSms, 'phone')}
                    {this.renderStatCard('Emails Sent', stats && stats.totalEmails, 'envelope')}
                    {this.renderStatCard('Faxes Sent', stats && stats.totalFaxes, 'print')}
                </Row>
                <Row>
                    <Col xs={12}>
                        <Panel data-hj-whitelist header={`All Users${users ? ` (${activeUsersCount} active)` : ''}`}>
                            <Row style={{ marginBottom: 10 }}>
                                <Col xs={12} md={4}>
                                    <TextInput field={{
                                                   name: 'adminUserFilter',
                                                   value: this.state.filter,
                                                   onChange: (evt) => this.setState({ filter: evt.target.value }),
                                               }}
                                               placeholder="Filter by name, email or id"
                                    />
                                </Col>
                                <Col xs={6} md={4}>
                                    <CheckBox field={{ name: 'showDemoAccounts' }}
                                              checked={this.state.showDemo}
                                              label=""
                                              style={{ float: 'left', marginRight: 10 }}
                                              onChange={() => this.setState({ showDemo: !this.state.showDemo })}>
                                        Show demo accounts ({demoCount})
                                    </CheckBox>
                                </Col>
                                <Col xs={6} md={4}>
                                    <div style={{ float: 'right' }}>
                                        <Button bsStyle="danger"
                                                label="Clear Demo Accounts"
                                                icon="trash"
                                                disabled={this.state.loading || tempDemoCount === 0}
                                                onClick={() => this.setState({ clearDemoDialog: true })} />
                                    </div>
                                </Col>
                            </Row>
                            <LoadingProgressBar loading={!users || this.state.loading} />
                            {users && (
                            <div style={{ overflowX: 'auto' }}>
                                <table className="table table-hover" style={{ marginBottom: 0 }}>
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>User</th>
                                            <th>Company</th>
                                            <th>Created</th>
                                            <th>Last Activity</th>
                                            <th style={{ textAlign: 'right' }}>Logins</th>
                                            <th style={{ textAlign: 'right' }}>Cases</th>
                                            <th style={{ textAlign: 'right' }}>Files</th>
                                            <th style={{ textAlign: 'right' }}>Size</th>
                                            <th style={{ textAlign: 'right' }}>SMS</th>
                                            <th style={{ textAlign: 'right' }}>Emails</th>
                                            <th style={{ textAlign: 'right' }}>Faxes</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows}
                                    </tbody>
                                </table>
                            </div>)}
                        </Panel>
                    </Col>
                </Row>
                {deleteCandidate && (
                <ConfirmDialog title="Delete user?"
                               warning
                               open
                               text={deleteIsOwner
                                   ? `Permanently delete OWNER account ${deleteCandidate['Email']} (id ${deleteCandidate.id}) including ALL its employees, cases and files? This cannot be undone.`
                                   : `Permanently delete employee ${deleteCandidate['Email']} (id ${deleteCandidate.id})? This cannot be undone.`}
                               cancel={() => this.setState({ deleteCandidate: undefined })}
                               confirm={this.deleteUser} />)}
                {this.state.clearDemoDialog && (
                <ConfirmDialog title="Clear demo accounts?"
                               warning
                               open
                               text={`Permanently purge all ${tempDemoCount} temporary demo visitor account(s) with their data and files?`}
                               cancel={() => this.setState({ clearDemoDialog: false })}
                               confirm={this.clearDemoAccounts} />)}
            </div>
        );
    }
}


const mapStateToProps = (state) => {
    return {
        loggedIn: state.user.loggedIn,
    };
};

export default connect(mapStateToProps)(AdminDashboard);
